import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router';
import { Toaster } from 'sonner';
import { Sentry } from '@/lib/sentry';
import { lazy, Suspense, useEffect } from 'react';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ArrangementProvider, useArrangement } from '@/context/ArrangementContext';
import AppLayout from '@/components/AppLayout';
import { LoadingScreen } from '@/components/Page';
import TawkChat from '@/components/TawkChat';

const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Settings = lazy(() => import('@/pages/Settings'));
const Tasks = lazy(() => import('@/pages/Tasks'));
const Budget = lazy(() => import('@/pages/Budget'));
const GuestList = lazy(() => import('@/pages/GuestList'));
const Program = lazy(() => import('@/pages/Program'));
const SeatingChart = lazy(() => import('@/pages/SeatingChart'));
const Vendors = lazy(() => import('@/pages/Vendors'));
const Login = lazy(() => import('@/pages/Login'));
const LoginMfa = lazy(() => import('@/pages/LoginMfa'));
const Register = lazy(() => import('@/pages/Register'));
const GrandMoments = lazy(() => import('@/pages/GrandMoments'));
const RegisterConfirm = lazy(() => import('@/pages/RegisterConfirm'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const NewPassword = lazy(() => import('@/pages/NewPassword'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Logout = lazy(() => import('./pages/Logout'));
const Profile = lazy(() => import('@/pages/Profile'));
const Invitations = lazy(() => import('@/pages/Invitations'));
const Website = lazy(() => import('@/pages/Website'));
const Wishlist = lazy(() => import('@/pages/Wishlist'));
const Rsvp = lazy(() => import('@/pages/Rsvp'));

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, mfaRequired } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user && location.pathname !== '/loggut') {
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />;
  }
  if (mfaRequired && location.pathname !== '/loggut') {
    const redirectTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login/mfa?redirectTo=${redirectTo}`} replace />;
  }
  // OAuth signups (Google) get created without consent — block the app until
  // they have confirmed the terms on /registrer/bekreft
  if (user && !user.user_metadata?.terms_accepted && location.pathname !== '/loggut') {
    return <Navigate to="/registrer/bekreft" replace />;
  }
  return <>{children}</>;
}

function GuestRoute({ children, redirectTo: fallback = '/' }: { children: React.ReactNode; redirectTo?: string }) {
  const { user, loading, mfaRequired } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (user) {
    const redirectTo = new URLSearchParams(location.search).get('redirectTo');
    const isInternalPath = redirectTo !== null && redirectTo.startsWith('/') && !redirectTo.startsWith('//');
    const target = isInternalPath ? redirectTo : fallback;
    if (mfaRequired) {
      return <Navigate to={`/login/mfa?redirectTo=${encodeURIComponent(target)}`} replace />;
    }
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}

function OnboardingRoute() {
  const { typeSlug } = useParams();
  return <Onboarding key={typeSlug ?? 'onboarding'} />;
}

// Users without any event get the standalone profile; everyone else gets it inside the app shell
function ProfileRoute() {
  const { arrangements, loading } = useArrangement();
  if (loading) return <LoadingScreen />;
  if (arrangements.length === 0) return <Profile standalone />;
  return <AppLayout />;
}

export function ComingSoon({ title }: { title: string }) {
  return (
    <div className="min-h-dvh flex items-center justify-center flex-col gap-3 text-center p-6">
      <span className="text-5xl">🚧</span>
      <h1 className="text-2xl font-display font-semibold">{title}</h1>
      <p className="text-muted-foreground">Denne modulen er under utvikling.</p>
      <a href="/" className="text-primary text-sm underline underline-offset-4">
        Tilbake
      </a>
    </div>
  );
}

const ROUTE_TITLES: { pattern: RegExp; title: string; withEvent?: boolean }[] = [
  { pattern: /^\/login$/, title: 'Logg inn' },
  { pattern: /^\/registrer$/, title: 'Registrer deg' },
  { pattern: /^\/grandmoments$/, title: 'Registrer deg' },
  { pattern: /^\/registrer\/bekreft$/, title: 'Fullfør registreringen' },
  { pattern: /^\/glemt-passord$/, title: 'Glemt passord' },
  { pattern: /^\/nytt-passord$/, title: 'Nytt passord' },
  { pattern: /^\/(registrer|glemt-passord|profil)\/verifiser$/, title: 'Bekrefter' },
  { pattern: /^\/personvern$/, title: 'Personvern' },
  { pattern: /^\/ny$/, title: 'Nytt arrangement' },
  { pattern: /^\/ny\/[^/]+$/, title: 'Nytt arrangement' },
  { pattern: /^\/profil$/, title: 'Profil' },
  { pattern: /^\/[^/]+\/[^/]+\/budsjett$/, title: 'Budsjett', withEvent: true },
  { pattern: /^\/[^/]+\/[^/]+\/leverandorer$/, title: 'Leverandører', withEvent: true },
  { pattern: /^\/[^/]+\/[^/]+\/sjekkliste$/, title: 'Sjekkliste', withEvent: true },
  { pattern: /^\/[^/]+\/[^/]+\/gjesteliste$/, title: 'Gjesteliste', withEvent: true },
  { pattern: /^\/[^/]+\/[^/]+\/invitasjoner$/, title: 'Invitasjoner', withEvent: true },
  { pattern: /^\/[^/]+\/[^/]+\/nettside$/, title: 'Nettside', withEvent: true },
  { pattern: /^\/[^/]+\/[^/]+\/onskeliste$/, title: 'Ønskeliste', withEvent: true },
  { pattern: /^\/rsvp\/[^/]+$/, title: 'Svar på invitasjon' },
  { pattern: /^\/[^/]+\/[^/]+\/program$/, title: 'Program', withEvent: true },
  { pattern: /^\/[^/]+\/[^/]+\/bordplassering$/, title: 'Bordplassering', withEvent: true },
  { pattern: /^\/[^/]+\/[^/]+\/innstillinger$/, title: 'Innstillinger', withEvent: true },
  { pattern: /^\/[^/]+\/[^/]+$/, title: 'Oversikt', withEvent: true },
];

function TitleManager() {
  const { pathname } = useLocation();
  const { arrangement } = useArrangement();

  useEffect(() => {
    const route = ROUTE_TITLES.find((r) => r.pattern.test(pathname));

    const parts: string[] = ['Den Dagen – Alt du trenger til den store dagen'];
    if (route?.withEvent && arrangement) parts.unshift(arrangement.name);
    if (route?.title) parts.unshift(route.title);

    document.title = parts.join(' | ');
  }, [pathname, arrangement]);

  return null;
}

function AppRoutes() {
  return (
    <>
      <TitleManager />
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public */}
          <Route path="/personvern" element={<Privacy />} />
          <Route path="/rsvp/:token" element={<Rsvp />} />
          <Route path="/login/mfa" element={<LoginMfa />} />
          <Route
            path="/login"
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            }
          />
          <Route
            path="/registrer"
            element={
              <GuestRoute>
                <Register />
              </GuestRoute>
            }
          />
          <Route
            path="/grandmoments"
            element={
              <GuestRoute>
                <GrandMoments />
              </GuestRoute>
            }
          />
          {/* Guards itself: requires a logged-in user without confirmed terms */}
          <Route path="/registrer/bekreft" element={<RegisterConfirm />} />
          <Route path="/registrer/verifiser" element={<VerifyEmail type="signup" />} />
          <Route path="/glemt-passord/verifiser" element={<VerifyEmail type="recovery" to="/nytt-passord" />} />
          <Route path="/profil/verifiser" element={<VerifyEmail type="email_change" />} />
          <Route
            path="/glemt-passord"
            element={
              <GuestRoute redirectTo="/profil">
                <ForgotPassword />
              </GuestRoute>
            }
          />
          <Route
            path="/nytt-passord"
            element={
              <AuthRoute>
                <NewPassword />
              </AuthRoute>
            }
          />

          {/* Auth — uten arrangement */}
          <Route
            path="/ny"
            element={
              <AuthRoute>
                <OnboardingRoute />
              </AuthRoute>
            }
          />
          <Route
            path="/ny/:typeSlug"
            element={
              <AuthRoute>
                <OnboardingRoute />
              </AuthRoute>
            }
          />

          <Route
            path="/loggut"
            element={
              <AuthRoute>
                <Logout />
              </AuthRoute>
            }
          />
          <Route
            path="/profil"
            element={
              <AuthRoute>
                <ProfileRoute />
              </AuthRoute>
            }
          >
            <Route index element={<Profile />} />
          </Route>

          {/* App — nestede ruter under /:eventType/:slug */}
          <Route
            path="/:eventType/:slug"
            element={
              <AuthRoute>
                <AppLayout />
              </AuthRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="budsjett" element={<Budget />} />
            <Route path="leverandorer" element={<Vendors />} />
            <Route path="sjekkliste" element={<Tasks />} />
            <Route path="gjoremal" element={<Navigate to="../sjekkliste" replace />} />
            <Route path="gjesteliste" element={<GuestList />} />
            <Route path="invitasjoner" element={<Invitations />} />
            <Route path="nettside" element={<Website />} />
            <Route path="onskeliste" element={<Wishlist />} />
            <Route path="program" element={<Program />} />
            <Route path="kjoreplan" element={<Navigate to="../program" replace />} />
            <Route path="bordplassering" element={<SeatingChart />} />
            <Route path="innstillinger" element={<Settings />} />
            <Route path="profil" element={<Navigate to="/profil" replace />} />
          </Route>

          {/* Root og fallback */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function RootRedirect() {
  const { user, loading: authLoading } = useAuth();
  const { arrangements, loading: arrangementsLoading } = useArrangement();

  if (authLoading || (user && arrangementsLoading)) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  if (arrangements.length === 0) return <Navigate to="/ny" replace />;

  // 1. Last used arrangement from localStorage
  const lastActiveId = localStorage.getItem(`dendagen_active_${user.id}`);
  if (lastActiveId) {
    const arr = arrangements.find((a) => a.id === lastActiveId);
    if (arr) return <Navigate to={`/${arr.eventType}/${arr.slug}`} replace />;
  }

  // 2. Nearest arrangement by event date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const distance = (date?: string) => (date ? Math.abs(new Date(date).getTime() - today.getTime()) : Number.MAX_SAFE_INTEGER);
  const nearest = [...arrangements].sort((a, b) => distance(a.date) - distance(b.date))[0];
  return <Navigate to={`/${nearest.eventType}/${nearest.slug}`} replace />;
}

function AppWithAuth() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return (
    <ArrangementProvider key={user?.id ?? 'guest'} userId={user?.id}>
      <AppRoutes />
    </ArrangementProvider>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
      <BrowserRouter>
        <AuthProvider>
          <TawkChat />
          <AppWithAuth />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </Sentry.ErrorBoundary>
  );
}
